import { differenceInCalendarDays } from 'date-fns';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';

export interface ISummaryLineItem {
  key: string;
  itemName: string;
  unitAbbreviation?: string;
  quantity: number;
  expiryDate: Date | null;
  batchNumber?: string;
  unitPrice: number;
  discount: number;
  totalAfterDiscount: number;
}

const EXPIRING_SOON_WITHIN_DAYS = 30;

function ExpiryLabel({ expiryDate }: { expiryDate: Date | null }) {
  if (!expiryDate) {
    return (
      <Label variant="soft" color="default">
        No expiry
      </Label>
    );
  }

  const daysToExpiry = differenceInCalendarDays(new Date(expiryDate), new Date());
  const isExpiringSoon = daysToExpiry <= EXPIRING_SOON_WITHIN_DAYS;

  return (
    <Label variant="soft" color={isExpiringSoon ? 'warning' : 'default'}>
      {fDate(expiryDate)}
    </Label>
  );
}

type Props = {
  items: ISummaryLineItem[];
};

export default function PurchaseItemsSummary({ items }: Props) {
  return (
    <Card sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 24 } }}>
      <Typography variant="subtitle1" sx={{ px: 1, pb: 1 }}>
        Items · {items.length}
      </Typography>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
          No items added yet
        </Typography>
      ) : (
        <Scrollbar sx={{ maxHeight: 560 }}>
          <Stack spacing={1.5} sx={{ p: 1 }}>
            {items.map((item) => (
              <Stack
                key={item.key}
                spacing={0.75}
                sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.neutral' }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
                    {item.itemName || 'Select item'}
                  </Typography>
                  <ExpiryLabel expiryDate={item.expiryDate} />
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {item.quantity} {item.unitAbbreviation ?? ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Batch: {item.batchNumber || '-'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Unit Price: {fCurrencyRupees(item.unitPrice * 100)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Discount: {fCurrencyRupees(item.discount * 100)}
                  </Typography>
                </Stack>

                <Divider sx={{ borderStyle: 'dashed' }} />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">Total</Typography>
                  <Typography variant="subtitle2">
                    {fCurrencyRupees(item.totalAfterDiscount * 100)}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Scrollbar>
      )}
    </Card>
  );
}
